import type {
  ScheduleUnit,
  Weekday,
} from "../components/organisms/DeliverStory/types";
import { WEEKDAYS } from "../components/organisms/DeliverStory/types";

export type { ScheduleUnit, Weekday };
export { WEEKDAYS };

/**
 * Common schedule fields shared by every schedule editor in the app.
 * Concrete config types (DeliverStory's `ScheduleConfig`, the connector
 * `ScheduleRefreshConfig`) extend this and add their own fields.
 */
export interface BaseScheduleConfig {
  every: number;
  unit: ScheduleUnit;
  repeatOn: Weekday[];
  refreshHour: number | null;
  timezone: string;
}

export type BaseScheduleField =
  | "every"
  | "repeatOn"
  | "refreshHour"
  | "timezone";

export type BaseScheduleErrors = Partial<Record<BaseScheduleField, string>>;

/** Today's calendar date as ISO `yyyy-mm-dd` in the user's local timezone. */
export const isoDateTodayLocal = (): string => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

/** Day of month (1–31) from an ISO date string, or null if invalid. */
export const dayOfMonthFromIso = (iso: string): number | null => {
  const parts = iso.trim().split("-");
  if (parts.length !== 3) return null;
  const dom = Number(parts[2]);
  if (!Number.isFinite(dom) || dom < 1 || dom > 31) return null;
  return dom;
};

/**
 * Next calendar date on or after today whose day-of-month matches ``repeatDom``
 * (clamped to each month's length). Used when hydrating monthly schedules from
 * API ``repeat_dom`` only so "Starts on" is never in the past.
 */
export const isoNextOrEqualTodayForDom = (repeatDom: number): string => {
  const todayStr = isoDateTodayLocal();
  const [ty, tm] = todayStr.split("-").map(Number);
  if (!ty || !tm) return todayStr;
  const lastThis = new Date(ty, tm, 0).getDate();
  const dayThis = Math.min(Math.max(1, repeatDom), lastThis);
  const candidate = `${ty}-${String(tm).padStart(2, "0")}-${String(dayThis).padStart(2, "0")}`;
  if (candidate >= todayStr) return candidate;
  const firstNext = new Date(ty, tm, 1);
  const ny = firstNext.getFullYear();
  const nm = firstNext.getMonth() + 1;
  const lastNext = new Date(ny, nm, 0).getDate();
  const dayNext = Math.min(Math.max(1, repeatDom), lastNext);
  return `${ny}-${String(nm).padStart(2, "0")}-${String(dayNext).padStart(2, "0")}`;
};

/**
 * Validate the four base fields shared by every schedule editor.
 * Specific consumers should call this then add their own field checks.
 */
export const validateBaseSchedule = (
  cfg: BaseScheduleConfig,
): BaseScheduleErrors => {
  const errors: BaseScheduleErrors = {};

  if (!Number.isFinite(cfg.every) || cfg.every < 1) {
    errors.every = "Must be at least 1";
  }

  if (cfg.unit === "week" && cfg.repeatOn.length === 0) {
    errors.repeatOn = "Select at least one day";
  }

  if (cfg.refreshHour === null) {
    errors.refreshHour = "Select a time";
  }

  if (!cfg.timezone) {
    errors.timezone = "Select a timezone";
  }

  return errors;
};
